import * as i0 from '@angular/core';
import { InjectionToken, Directive, Optional, Inject, Input, HostListener, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

const AUTO_SIZE_INPUT_OPTIONS = new InjectionToken('autosize-input-options');
const DEFAULT_AUTO_SIZE_INPUT_OPTIONS = {
    extraWidth: 0,
    includeBorders: false,
    includePadding: true,
    includePlaceholder: true,
    maxWidth: -1,
    minWidth: -1,
    setParentWidth: false,
    usePlaceHolderWhenEmpty: false,
};

var Border;
(function (Border) {
    Border["left"] = "border-left-width";
    Border["right"] = "border-right-width";
})(Border || (Border = {}));

var Padding;
(function (Padding) {
    Padding["left"] = "padding-left";
    Padding["right"] = "padding-right";
})(Padding || (Padding = {}));

class AutoSizeInputDirective {
    constructor(element, renderer, ngModel, ngControl, options) {
        this.element = element;
        this.renderer = renderer;
        this.ngModel = ngModel;
        this.ngControl = ngControl;
        this.options = options;
        this.useValueProperty = false;
        this.destroyed$ = new Subject();
        const defaultOptions = this.options || DEFAULT_AUTO_SIZE_INPUT_OPTIONS;
        this.extraWidth = defaultOptions.extraWidth;
        this.includeBorders = defaultOptions.includeBorders;
        this.includePadding = defaultOptions.includePadding;
        this.includePlaceholder = defaultOptions.includePlaceholder;
        this.maxWidth = defaultOptions.maxWidth;
        this.minWidth = defaultOptions.minWidth;
        this.setParentWidth = defaultOptions.setParentWidth;
        this.usePlaceHolderWhenEmpty = defaultOptions.usePlaceHolderWhenEmpty;
    }
    get borderWidth() {
        return this.includeBorders
            ? this.sumStylePropertyWidths(Border.left, Border.right)
            : 0;
    }
    get paddingWidth() {
        return this.includePadding
            ? this.sumStylePropertyWidths(Padding.left, Padding.right)
            : 0;
    }
    get style() {
        return getComputedStyle(this.element.nativeElement, '');
    }
    get placeholder() {
        return this.element.nativeElement.placeholder;
    }
    get value() {
        return this.element.nativeElement.value;
    }
    get inputWidthIsLessThanMinimum() {
        return this.minWidth > 0 && this.minWidth > this.inputTextWidth;
    }
    get inputWidthIsGreaterThanMaximum() {
        return this.maxWidth > 0 && this.maxWidth < this.inputTextWidth;
    }
    get inputTextWidth() {
        return (this.getTextWidth(this.input) +
            this.extraWidth +
            this.borderWidth +
            this.paddingWidth);
    }
    get usePlaceholder() {
        return (this.includePlaceholder &&
            this.hasPlaceholder &&
            this.placeHolderWidthGreaterThanInputWidth &&
            (this.inputIsEmpty || !this.usePlaceHolderWhenEmpty));
    }
    get placeHolderWidthGreaterThanInputWidth() {
        const placeHolderWidth = this.getTextWidth(this.placeholder);
        const inputWidth = this.getTextWidth(this.input);
        return placeHolderWidth > inputWidth;
    }
    get inputIsEmpty() {
        return this.input.length === 0;
    }
    get hasPlaceholder() {
        return this.placeholder.length > 0;
    }
    get input() {
        let value = '';
        if (this.useValueProperty) {
            value = this.value;
        }
        else if (this.ngModel) {
            value = this.ngModel.value;
        }
        else if (this.ngControl) {
            value = this.ngControl.value;
        }
        return value || this.value || '';
    }
    get nativeElement() {
        return this.element.nativeElement;
    }
    get textWidth() {
        const text = this.usePlaceholder ? this.placeholder : this.input;
        return (this.borderWidth +
            this.extraWidth +
            this.getTextWidth(text) +
            this.paddingWidth);
    }
    ngAfterViewInit() {
        this.updateWidthWhenModelChanges();
        this.updateWidthWhenControlChanges();
        this.updateWidth();
    }
    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
    onInput() {
        if (!this.ngModel && !this.ngControl) {
            this.updateWidth();
        }
    }
    setWidth(width) {
        const parent = this.renderer.parentNode(this.nativeElement);
        if (this.setParentWidth) {
            this.renderer.setStyle(parent, 'width', width + 'px');
        }
        else {
            this.renderer.setStyle(this.nativeElement, 'width', width + 'px');
        }
    }
    getTextWidth(value) {
        const element = this.renderer.createElement('canvas');
        const context = element.getContext('2d');
        const { fontStyle, fontVariant, fontWeight, fontSize, fontFamily, letterSpacing, } = this.style;
        // font string format: {normal, normal, 700, 20px, Roboto, "Helvetica Neue", sans-serif}
        context.font = `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize} ${fontFamily}`;
        context.letterSpacing = letterSpacing;
        const width = context.measureText(value).width;
        element.remove();
        return width;
    }
    updateWidth() {
        if (this.inputWidthIsLessThanMinimum) {
            this.setWidth(this.minWidth);
        }
        else if (this.inputWidthIsGreaterThanMaximum) {
            this.setWidth(this.maxWidth);
        }
        else {
            this.setWidth(this.textWidth);
        }
    }
    updateWidthWhenControlChanges() {
        return this.ngControl?.valueChanges
            ?.pipe(tap(() => this.updateWidth()), takeUntil(this.destroyed$))
            .subscribe();
    }
    updateWidthWhenModelChanges() {
        return this.ngModel?.valueChanges
            ?.pipe(tap(() => this.updateWidth()), takeUntil(this.destroyed$))
            .subscribe();
    }
    sumStylePropertyWidths(...properties) {
        return properties.reduce((sum, property) => {
            const value = this.style.getPropertyValue(property);
            const width = parseInt(value, 10);
            return sum + width;
        }, 0);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutoSizeInputDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1.NgModel, optional: true }, { token: i1.NgControl, optional: true }, { token: AUTO_SIZE_INPUT_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.9", type: AutoSizeInputDirective, selector: "[autoSizeInput]", inputs: { extraWidth: "extraWidth", includeBorders: "includeBorders", includePadding: "includePadding", includePlaceholder: "includePlaceholder", maxWidth: "maxWidth", minWidth: "minWidth", setParentWidth: "setParentWidth", usePlaceHolderWhenEmpty: "usePlaceHolderWhenEmpty", useValueProperty: "useValueProperty" }, host: { listeners: { "input": "onInput()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutoSizeInputDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[autoSizeInput]',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.NgModel, decorators: [{
                    type: Optional
                }] }, { type: i1.NgControl, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [AUTO_SIZE_INPUT_OPTIONS]
                }] }], propDecorators: { extraWidth: [{
                type: Input
            }], includeBorders: [{
                type: Input
            }], includePadding: [{
                type: Input
            }], includePlaceholder: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], setParentWidth: [{
                type: Input
            }], usePlaceHolderWhenEmpty: [{
                type: Input
            }], useValueProperty: [{
                type: Input
            }], onInput: [{
                type: HostListener,
                args: ['input']
            }] } });

class AutoSizeInputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutoSizeInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: AutoSizeInputModule, declarations: [AutoSizeInputDirective], imports: [CommonModule], exports: [AutoSizeInputDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutoSizeInputModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutoSizeInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [AutoSizeInputDirective],
                    exports: [AutoSizeInputDirective],
                }]
        }] });

class CdkInputAutosizeDirective {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
        this.cdkInputAutosizeUsePlaceHolderOnlyWhenEmpty = true;
        this._initialWidth = '0px';
    }
    get _inputElement() {
        return this._elementRef.nativeElement;
    }
    ngAfterViewInit() {
        this._resizeToFitContent();
    }
    ngDoCheck() {
        this._resizeToFitContent();
    }
    _createInputWithValue(value) {
        const clone = this._inputElement.cloneNode(false);
        clone.value = value;
        clone.style.position = 'absolute';
        clone.style.border = 'none';
        clone.style.padding = '0';
        clone.style.height = '';
        clone.style.minWidth = '';
        clone.style.width = '0px';
        clone.style.maxWidth = '';
        this._inputElement.parentNode?.appendChild(clone);
        return clone;
    }
    _getWidths() {
        const inputClone = this._createInputWithValue(this._inputElement.value);
        const placeHolderClone = this._createInputWithValue(this._inputElement.placeholder);
        const widths = { inputWidth: inputClone.scrollWidth, placeholderWidth: placeHolderClone.scrollWidth };
        inputClone.remove();
        placeHolderClone.remove();
        return widths;
    }
    _resizeToFitContent() {
        const { inputWidth, placeholderWidth } = this._getWidths();
        let width;
        if (this.cdkInputAutosizeUsePlaceHolderOnlyWhenEmpty) {
            width = this._inputElement.value === '' ? placeholderWidth : inputWidth;
        }
        else {
            width = placeholderWidth >= inputWidth ? placeholderWidth : inputWidth;
        }
        if (this.cdkInputAutosizeMinWidth !== undefined) {
            width = Math.max(width, this.cdkInputAutosizeMinWidth);
        }
        if (this.cdkInputAutosizeMaxWidth !== undefined) {
            width = Math.min(width, this.cdkInputAutosizeMaxWidth);
        }
        this._elementRef.nativeElement.style.width = width + 'px';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CdkInputAutosizeDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.9", type: CdkInputAutosizeDirective, isStandalone: true, selector: "input[cdkInputAutosize]", inputs: { cdkInputAutosizeMinWidth: "cdkInputAutosizeMinWidth", cdkInputAutosizeMaxWidth: "cdkInputAutosizeMaxWidth", cdkInputAutosizeUsePlaceHolderOnlyWhenEmpty: "cdkInputAutosizeUsePlaceHolderOnlyWhenEmpty" }, host: { listeners: { "input": "_resizeToFitContent()" }, properties: { "style.width": "_initialWidth" }, classAttribute: "cdk-input-autosize" }, exportAs: ["cdkInputAutosize"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: CdkInputAutosizeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[cdkInputAutosize]',
                    exportAs: 'cdkInputAutosize',
                    host: {
                        'class': 'cdk-input-autosize',
                        '[style.width]': '_initialWidth',
                        '(input)': '_resizeToFitContent()',
                    },
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { cdkInputAutosizeMinWidth: [{
                type: Input
            }], cdkInputAutosizeMaxWidth: [{
                type: Input
            }], cdkInputAutosizeUsePlaceHolderOnlyWhenEmpty: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { AUTO_SIZE_INPUT_OPTIONS, AutoSizeInputDirective, AutoSizeInputModule, CdkInputAutosizeDirective, DEFAULT_AUTO_SIZE_INPUT_OPTIONS };
